/**
 * Shared helpers for the URL download flow (YouTube + generic sites such as
 * X/Twitter, Facebook and Threads).
 */

const YT_HOSTS = ["youtube.com", "youtu.be", "music.youtube.com", "m.youtube.com"];

/**
 * Clean up whatever the user pasted (quotes, trailing punctuation, missing
 * scheme, a URL buried in a share message) or return null.
 */
export function normalizeDownloadUrl(input: string): string | null {
  if (!input) return null;
  let value = input.trim().replace(/^["'<]+|["'>]+$/g, "");
  if (!value) return null;

  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
    if (value.startsWith("//")) value = `https:${value}`;
    else if (/^[\w-]+(\.[\w-]+)+/.test(value)) value = `https://${value}`;
    else {
      const match = value.match(/https?:\/\/\S+/i);
      if (!match) return null;
      value = match[0];
    }
  }

  try {
    const parsed = new URL(value);
    if (!/^https?:$/.test(parsed.protocol)) return null;
    parsed.hash = "";
    return parsed.toString().replace(/[)\],;]+$/, "");
  } catch {
    return null;
  }
}

export function isYouTubeUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase().replace(/^www\./, "");
    return YT_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

export function isPlaylistUrl(url: string): boolean {
  return /playlist\?list=|&list=/.test(url);
}

/** Backend endpoint that handles this URL. */
export function downloadEndpoint(url: string, backendUrl = ""): string {
  return isYouTubeUrl(url) ? `${backendUrl}/api/download` : `${backendUrl}/api/download/site`;
}

/** Friendly label for the site a URL belongs to (mirrors backend sitePresets). */
export function siteLabel(url: string): string {
  try {
    const host = new URL(url).hostname.toLowerCase().replace(/^www\./, "");
    if (isYouTubeUrl(url)) return "YouTube";
    if (/(^|\.)x\.com$|twitter\.com$/.test(host)) return "X / Twitter";
    if (/(^|\.)facebook\.com$|^fb\.watch$|(^|\.)fb\.com$/.test(host)) return "Facebook";
    if (/(^|\.)threads\.(net|com)$/.test(host)) return "Threads";
    if (/(^|\.)instagram\.com$/.test(host)) return "Instagram";
    if (/(^|\.)tiktok\.com$/.test(host)) return "TikTok";
    if (/(^|\.)reddit\.com$/.test(host)) return "Reddit";
    if (/(^|\.)bilibili\.com$/.test(host)) return "Bilibili";
    return host;
  } catch {
    return "site";
  }
}

/**
 * Turn an axios failure from the download endpoints into one readable line.
 * The backend sends { error, code } where error is already user-facing
 * ("Facebook requires a logged-in session — export cookies ...").
 */
export function downloadErrorMessage(error: any, url?: string): string {
  const data = error?.response?.data;
  const site = url ? siteLabel(url) : null;
  if (data && (data.error || data.message)) {
    const message = data.error || data.message;
    const details = data.details && typeof data.details === "string" ? ` (${data.details})` : "";
    return `${message}${details}`;
  }
  if (error?.code === "ECONNABORTED") return `Timed out while starting the ${site || ""} download. Try again.`;
  if (error?.message) return error.message;
  return `Could not start the ${site || "video"} download.`;
}
